/**
 * FilterFx — transient screen filters for big-moment juice (Tier B).
 *
 * Render-only (unlinted src/fx). Attaches a filter to the target Container ONLY during an
 * event, then detaches — filters have a GPU cost (they re-render the target to a texture),
 * so they must never be always-on. Bounded to the viewport via filterArea so the RT is
 * screen-sized (not the whole level) and the HUD (a sibling layer) is untouched.
 *
 *   • shockwave(x,y) — a ripple from a kill/stomp point (screen px). ~0.7 s.
 *   • hurt()         — a brief RGB-split (chromatic aberration) pulse on damage. ~0.25 s.
 *
 * Determinism: purely cosmetic, no sim feedback — safe to run on wall-clock render dt.
 */
import { Container, type Filter, Rectangle } from 'pixi.js';
import { RGBSplitFilter, ShockwaveFilter } from 'pixi-filters';

export class FilterFx {
  reduced = false;
  private shock = new ShockwaveFilter({ amplitude: 10, wavelength: 120, speed: 700, brightness: 1.0 });
  private rgb = new RGBSplitFilter({ red: { x: 0, y: 0 }, green: { x: 0, y: 0 }, blue: { x: 0, y: 0 } });
  private shockActive = false;
  private rgbAmt = 0;
  /** filterArea is in the target's LOCAL space. `target` (worldLayer) is translated by
   *  -camera each frame, so a fixed (0,0,w,h) rect drifts off the viewport as the camera
   *  scrolls → the off-rect part of the world blanks during a filter pass. We instead track
   *  the viewport every frame via setViewport(): origin = -worldLayer.x/y (= the camera pos
   *  in local space), so the rect always covers exactly what's on screen. Mutated in place
   *  (Pixi re-reads it each frame) to avoid a per-frame Rectangle alloc. */
  private area = new Rectangle(0, 0, 1, 1);

  constructor(private target: Container) {
    this.target.filterArea = this.area;
  }

  /** Track the viewport in the target's local space (call every render with -worldLayer.x/y). */
  setViewport(x: number, y: number, w: number, h: number): void {
    this.area.x = x; this.area.y = y; this.area.width = w; this.area.height = h;
  }

  private sync(): void {
    const f: Filter[] = [];
    if (this.shockActive) f.push(this.shock);
    if (this.rgbAmt > 0) f.push(this.rgb);
    this.target.filters = f;
  }

  /** Ripple out from a screen-space point (world px minus camera). */
  shockwave(screenX: number, screenY: number): void {
    if (this.reduced) return;
    this.shock.center = { x: screenX, y: screenY };
    this.shock.time = 0;
    this.shockActive = true;
    this.sync();
  }

  /** RGB-split chromatic pulse (damage). */
  hurt(): void {
    if (this.reduced) return;
    this.rgbAmt = 1;
    this.applyRgb();
    this.sync();
  }

  private applyRgb(): void {
    const a = this.rgbAmt * 7; // px offset at full strength
    this.rgb.redX = -a;
    this.rgb.blueX = a;
  }

  update(dt: number): void {
    const d = dt > 0.05 ? 0.05 : dt;
    let changed = false;
    if (this.shockActive) {
      this.shock.time += d;
      if (this.shock.time > 0.45) { this.shockActive = false; changed = true; } // smaller reach = subtler
    }
    if (this.rgbAmt > 0) {
      this.rgbAmt -= d * 4.5;
      if (this.rgbAmt <= 0) { this.rgbAmt = 0; changed = true; }
      this.applyRgb();
    }
    if (changed) this.sync();
  }
}
